/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(function() {
	var SIZE_GUIDE_CATEGORIES = ['women', 'men', 'girls', 'boys'];
	var sizeGuideContentPromise = null;

	function syncEnhancedPurchaseUI() {
		document.querySelectorAll('[data-product-purchase]').forEach(function(container) {
			var productInfo = container.closest('.product__info');

			if (productInfo) {
				productInfo.classList.add('has-product-purchase-ui');
			}

			syncPurchaseOptionState(container);
		});
	}

	function getProductVariants(container) {
		if (!container) {
			return [];
		}

		if (container.__productVariants) {
			return container.__productVariants;
		}

		var variantsScript = container.querySelector('[data-product-variants]');

		if (!variantsScript) {
			container.__productVariants = [];
			return container.__productVariants;
		}

		try {
			container.__productVariants = JSON.parse(variantsScript.textContent || '[]');
		} catch (error) {
			container.__productVariants = [];
		}

		return container.__productVariants;
	}

	function getActivePurchaseButton(container, selector) {
		return container ? container.querySelector(selector + '.is-active') : null;
	}

	function setActivePurchaseButton(container, selector, activeButton) {
		if (!container) {
			return;
		}

		container.querySelectorAll(selector).forEach(function(button) {
			button.classList.toggle('is-active', button === activeButton);
		});
	}

	function getPurchaseSelections(container) {
		var selections = {};
		var activeColor = getActivePurchaseButton(container, '[data-sample-color]');
		var activeSize = getActivePurchaseButton(container, '[data-sample-size]');

		if (activeColor) {
			selections[activeColor.getAttribute('data-option-position')] = activeColor.getAttribute('data-option-value');
		}

		if (activeSize) {
			selections[activeSize.getAttribute('data-option-position')] = activeSize.getAttribute('data-option-value');
		}

		return selections;
	}

	function variantMatchesSelections(variant, selections) {
		return Object.keys(selections).every(function(positionKey) {
			var optionIndex = parseInt(positionKey, 10) - 1;
			return optionIndex >= 0 && variant.options && variant.options[optionIndex] === selections[positionKey];
		});
	}

	function findMatchingVariant(variants, selections, onlyAvailable) {
		return variants.find(function(variant) {
			if (onlyAvailable && !variant.available) {
				return false;
			}

			return variantMatchesSelections(variant, selections);
		}) || null;
	}

	function updateVariantIdInput(form, variant) {
		if (!form || !variant) {
			return;
		}

		var variantIdInput = form.querySelector('[name="id"]');

		if (!variantIdInput) {
			variantIdInput = document.createElement('input');
			variantIdInput.type = 'hidden';
			variantIdInput.name = 'id';
			form.appendChild(variantIdInput);
		}

		variantIdInput.disabled = false;
		variantIdInput.value = String(variant.id);
	}

	function syncOptionAvailability(container, selector, selections) {
		var variants = getProductVariants(container);

		container.querySelectorAll(selector).forEach(function(button) {
			var optionPosition = button.getAttribute('data-option-position');
			var optionValue = button.getAttribute('data-option-value');
			var candidateSelections = Object.assign({}, selections);
			candidateSelections[optionPosition] = optionValue;
			var hasAvailableVariant = !!findMatchingVariant(variants, candidateSelections, true);

			button.classList.toggle('is-unavailable', !hasAvailableVariant);
			button.disabled = !hasAvailableVariant;
			button.setAttribute('aria-disabled', hasAvailableVariant ? 'false' : 'true');
		});
	}

	function ensureAvailablePurchaseSelection(container, selector, selections) {
		var activeButton = getActivePurchaseButton(container, selector);
		var fallbackButton = Array.from(container.querySelectorAll(selector)).find(function(button) {
			return !button.classList.contains('is-unavailable');
		});
		var nextButton = activeButton && !activeButton.classList.contains('is-unavailable') ? activeButton : fallbackButton;

		if (!nextButton) {
			return false;
		}

		setActivePurchaseButton(container, selector, nextButton);
		selections[nextButton.getAttribute('data-option-position')] = nextButton.getAttribute('data-option-value');
		return true;
	}

	function syncPurchaseOptionState(container) {
		if (!container) {
			return;
		}

		var variants = getProductVariants(container);
		var form = getProductForm(container);
		var hasColorOptions = container.querySelector('[data-sample-color]');
		var hasSizeOptions = container.querySelector('[data-sample-size]');
		var selections = getPurchaseSelections(container);

		if (!variants.length) {
			return;
		}

		for (var attempt = 0; attempt < 2; attempt += 1) {
			if (hasColorOptions) {
				syncOptionAvailability(container, '[data-sample-color]', selections);
				ensureAvailablePurchaseSelection(container, '[data-sample-color]', selections);
			}

			if (hasSizeOptions) {
				syncOptionAvailability(container, '[data-sample-size]', selections);
				ensureAvailablePurchaseSelection(container, '[data-sample-size]', selections);
			}
		}

		var activeColor = getActivePurchaseButton(container, '[data-sample-color]');
		var colorNameElement = container.querySelector('[data-sample-color-name]');

		if (colorNameElement && activeColor) {
			colorNameElement.textContent = activeColor.getAttribute('data-option-value');
		}

		var matchingVariant = findMatchingVariant(variants, selections, true) || findMatchingVariant(variants, selections, false);
		var sampleAddButton = container.querySelector('[data-sample-add]');

		if (matchingVariant) {
			updateVariantIdInput(form, matchingVariant);
		}

		if (sampleAddButton) {
			sampleAddButton.disabled = !(matchingVariant && matchingVariant.available);
		}
	}

	function detectSizeGuideCategory(table, fallbackCategory) {
		var contextText = '';
		var previousElement = table.previousElementSibling;
		var scanCount = 0;

		if (table.caption) {
			contextText += ' ' + table.caption.textContent;
		}

		if (table.tHead) {
			contextText += ' ' + table.tHead.textContent;
		}

		if (table.tBodies && table.tBodies[0]) {
			contextText += ' ' + table.tBodies[0].textContent;
		}

		contextText += ' ' + (table.textContent || '');

		while (previousElement && scanCount < 5) {
			contextText += ' ' + previousElement.textContent;
			previousElement = previousElement.previousElementSibling;
			scanCount += 1;
		}

		contextText = contextText.toLowerCase();

		if (/\bboys?\b|\bboy\s*thobe\b|\bkids?\s*boys?\b/.test(contextText)) {
			return 'boys';
		}

		if (/\bgirls?\b|\bgirl\s*abaya\b|\bkids?\s*girls?\b/.test(contextText)) {
			return 'girls';
		}

		if (/\bmen\b|\bmale\b|\bgents?\b|\bthobes?\b/.test(contextText)) {
			return 'men';
		}

		if (/\bwomen\b|\bfemale\b|\blad(?:y|ies)\b|\babayas?\b/.test(contextText)) {
			return 'women';
		}

		for (var i = 0; i < SIZE_GUIDE_CATEGORIES.length; i += 1) {
			if (contextText.indexOf(SIZE_GUIDE_CATEGORIES[i]) !== -1) {
				return SIZE_GUIDE_CATEGORIES[i];
			}
		}

		return fallbackCategory;
	}

	function extractCategoryTables(rootElement) {
		var tableMap = { women: '', men: '', girls: '', boys: '' };

		if (!rootElement) {
			return tableMap;
		}

		Array.from(rootElement.querySelectorAll('table')).forEach(function(table, index) {
			var fallbackCategory = SIZE_GUIDE_CATEGORIES[index] || SIZE_GUIDE_CATEGORIES[0];
			var detectedCategory = detectSizeGuideCategory(table, fallbackCategory);

			if (!tableMap[detectedCategory]) {
				tableMap[detectedCategory] = table.outerHTML;
			}
		});

		return tableMap;
	}

	function mergeCategoryTables(primaryMap, fallbackMap) {
		var merged = { women: '', men: '', girls: '', boys: '' };

		SIZE_GUIDE_CATEGORIES.forEach(function(category) {
			merged[category] = (primaryMap && primaryMap[category]) || (fallbackMap && fallbackMap[category]) || '';
		});

		return merged;
	}

	function firstAvailableCategory(tableMap) {
		for (var i = 0; i < SIZE_GUIDE_CATEGORIES.length; i += 1) {
			var category = SIZE_GUIDE_CATEGORIES[i];

			if (tableMap && tableMap[category]) {
				return category;
			}
		}

		return SIZE_GUIDE_CATEGORIES[0];
	}

	function normalizeTargetGender(value) {
		var normalized = (value || '').toString().toLowerCase();
		normalized = normalized.replace(/['’]/g, '').replace(/[^a-z\s]/g, ' ').replace(/\s+/g, ' ').trim();

		if (/\bboys?\b|\bboy\b/.test(normalized)) {
			return 'boys';
		}

		if (/\bgirls?\b|\bgirl\b/.test(normalized)) {
			return 'girls';
		}

		if (/\bmen\b|\bmens\b|\bmale\b|\bgents?\b|\bman\b/.test(normalized)) {
			return 'men';
		}

		if (/\bwomen\b|\bwomens\b|\bfemale\b|\blad(?:y|ies)\b|\bwoman\b/.test(normalized)) {
			return 'women';
		}

		return '';
	}

	function updateSizeGuideTabVisibility(dialog) {
		var tableMap = dialog ? dialog._sizeGuideTables : null;
		var targetGender = dialog ? dialog._targetGender : '';

		if (!dialog || !tableMap) {
			return;
		}

		dialog.querySelectorAll('[data-size-guide-tab]').forEach(function(tabButton) {
			var category = tabButton.getAttribute('data-size-guide-tab');

			if (targetGender) {
				tabButton.classList.toggle('is-hidden', category !== targetGender);
				return;
			}

			tabButton.classList.toggle('is-hidden', !tableMap[category]);
		});
	}

	function renderSizeGuideTable(dialog, category) {
		if (!dialog) {
			return;
		}

		var rte = dialog.querySelector('.product-size-guide-dialog__rte');
		var tableMap = dialog._sizeGuideTables || {};
		var tableMarkup = tableMap[category] || '';

		if (!rte) {
			return;
		}

		if (tableMarkup) {
			rte.innerHTML = tableMarkup;
		} else {
			rte.innerHTML = '<p class="product-size-guide-dialog__empty">Size chart is not available for this category.</p>';
		}
	}

	function fetchSizeGuideTables() {
		if (sizeGuideContentPromise) {
			return sizeGuideContentPromise;
		}

		sizeGuideContentPromise = fetch('/pages/size-chart', { credentials: 'same-origin' }).then(function(response) {
			if (!response.ok) {
				throw new Error('Unable to load size guide page');
			}

			return response.text();
		}).then(function(html) {
			var parser = new DOMParser();
			var pageDocument = parser.parseFromString(html, 'text/html');
			var pageRoot = pageDocument.querySelector('main .rte') || pageDocument.querySelector('.page__content .rte') || pageDocument.querySelector('.rte') || pageDocument.body;

			return extractCategoryTables(pageRoot);
		}).catch(function() {
			return null;
		});

		return sizeGuideContentPromise;
	}

	function applySizeGuideTabState(dialog, category) {
		if (!dialog || !category) {
			return;
		}

		dialog.querySelectorAll('[data-size-guide-tab]').forEach(function(tabButton) {
			var isActive = tabButton.getAttribute('data-size-guide-tab') === category;

			tabButton.classList.toggle('is-active', isActive);
			tabButton.setAttribute('aria-selected', isActive ? 'true' : 'false');
		});

		renderSizeGuideTable(dialog, category);
	}

	function initSizeGuideTabs() {
		document.querySelectorAll('.product-size-guide-dialog').forEach(function(dialog) {
			if (dialog.getAttribute('data-size-guide-bound') === 'true') {
				return;
			}

			var rte = dialog.querySelector('.product-size-guide-dialog__rte');

			if (!rte) {
				return;
			}

			dialog._sizeGuideTables = extractCategoryTables(rte);
			dialog._targetGender = normalizeTargetGender(dialog.getAttribute('data-target-gender'));
			dialog.classList.add('product-size-guide-dialog--single-category');
			updateSizeGuideTabVisibility(dialog);
			var targetGender = dialog._targetGender;
			var initialCategory = targetGender && dialog._sizeGuideTables[targetGender] ? targetGender : firstAvailableCategory(dialog._sizeGuideTables);
			applySizeGuideTabState(dialog, initialCategory);
			dialog.setAttribute('data-size-guide-bound', 'true');

			fetchSizeGuideTables().then(function(remoteTableMap) {
				if (!remoteTableMap || !dialog.isConnected) {
					return;
				}

				dialog._sizeGuideTables = mergeCategoryTables(remoteTableMap, dialog._sizeGuideTables);
				dialog._targetGender = normalizeTargetGender(dialog.getAttribute('data-target-gender'));
				dialog.classList.add('product-size-guide-dialog--single-category');
				updateSizeGuideTabVisibility(dialog);

				var activeTab = dialog.querySelector('[data-size-guide-tab].is-active:not(.is-hidden)');
				var fallbackCategory = firstAvailableCategory(dialog._sizeGuideTables);
				var preferredCategory = dialog._targetGender ? dialog._targetGender : fallbackCategory;
				var activeCategory = activeTab ? activeTab.getAttribute('data-size-guide-tab') : preferredCategory;
				applySizeGuideTabState(dialog, activeCategory);
			});
		});
	}

	function getDefaultSizeGuideOffset() {
		if (window.innerWidth <= 640) {
			return 72;
		}

		if (window.innerWidth <= 999) {
			return 88;
		}

		return 120;
	}

	function updateSizeGuideDialogOffset() {
		var selectors = [
			'.shopify-section-group-header-group',
			'#shopify-section-header',
			'.shopify-section--header',
			'header'
		];
		var seen = [];
		var maxHeight = 0;

		selectors.forEach(function(selector) {
			document.querySelectorAll(selector).forEach(function(element) {
				if (seen.indexOf(element) !== -1) {
					return;
				}

				seen.push(element);

				if (getComputedStyle(element).display === 'none' || element.offsetHeight === 0) {
					return;
				}

				var rect = element.getBoundingClientRect();
				var candidateHeight = Math.max(rect.height, element.offsetHeight);

				if (candidateHeight > maxHeight) {
					maxHeight = candidateHeight;
				}
			});
		});

		var baseOffset = getDefaultSizeGuideOffset();
		var finalOffset = maxHeight > 0 ? Math.min(Math.max(Math.round(maxHeight + 12), baseOffset), 240) : baseOffset;

		document.documentElement.style.setProperty('--size-guide-top-offset', finalOffset + 'px');
	}

	function getProductForm(container) {
		if (!container) {
			return null;
		}

		var formId = container.getAttribute('data-product-form-id');

		if (!formId) {
			return null;
		}

		return document.getElementById(formId);
	}

	function setFormQuantity(form, quantity) {
		if (!form || !quantity || quantity < 1) {
			return;
		}

		var quantityInput = form.querySelector('input[name="quantity"]');

		if (!quantityInput) {
			quantityInput = document.createElement('input');
			quantityInput.type = 'hidden';
			quantityInput.name = 'quantity';
			form.appendChild(quantityInput);
		}

		quantityInput.value = String(quantity);
	}

	function submitProductForm(form) {
		if (!form) {
			return;
		}

		if (typeof form.requestSubmit === 'function') {
			form.requestSubmit();
		} else {
			form.submit();
		}
	}

	function setVariantOption(container, optionPosition, optionValue) {
		if (!container || !optionPosition || !optionValue) {
			return;
		}

		var form = getProductForm(container);
		var formId = form ? form.id : '';
		var optionSelector = '[name="option' + optionPosition + '"]' + (formId ? '[form="' + formId + '"]' : '');
		var optionInputs = Array.from(document.querySelectorAll(optionSelector));
		var didSet = false;

		optionInputs.forEach(function(input) {
			if (didSet || input.disabled) {
				return;
			}

			if ((input.type === 'radio' || input.type === 'checkbox') && input.value == optionValue) {
				input.checked = true;
				input.dispatchEvent(new Event('change', { bubbles: true }));
				didSet = true;
				return;
			}

			if ((input.tagName === 'SELECT' || input.tagName === 'INPUT') && input.value != optionValue) {
				input.value = optionValue;
				input.dispatchEvent(new Event('change', { bubbles: true }));
				didSet = true;
			}
		});
	}

	function animateModeTransition(outgoingPanel, incomingPanel) {
		if (!outgoingPanel || !incomingPanel) {
			return;
		}

		incomingPanel.classList.remove('is-hidden');
		incomingPanel.style.opacity = '0';
		incomingPanel.style.transform = 'translateY(16px) scale(0.98)';

		requestAnimationFrame(function() {
			var easing = 'cubic-bezier(0.34, 1.56, 0.64, 1)';
			outgoingPanel.style.transition = 'opacity 400ms ' + easing + ', transform 400ms ' + easing;
			incomingPanel.style.transition = 'opacity 400ms ' + easing + ', transform 400ms ' + easing;
			outgoingPanel.style.opacity = '0';
			outgoingPanel.style.transform = 'translateY(-16px) scale(0.98)';
			incomingPanel.style.opacity = '1';
			incomingPanel.style.transform = 'translateY(0) scale(1)';

			window.setTimeout(function() {
				outgoingPanel.classList.add('is-hidden');
				outgoingPanel.style.transition = '';
				outgoingPanel.style.opacity = '';
				outgoingPanel.style.transform = '';
				incomingPanel.style.transition = '';
				incomingPanel.style.opacity = '';
				incomingPanel.style.transform = '';
			}, 400);
		});
	}

	function switchMode(container, mode) {
		// Scroll to top for better UX when switching modes
		var productSection = container.closest('.product__info') || container;
		var scrollTarget = productSection.getBoundingClientRect().top + window.scrollY - 100;
		window.scrollTo({
			top: Math.max(0, scrollTarget),
			behavior: 'smooth'
		});

		var wholesalePanel = container.querySelector('[data-mode-panel="wholesale"]');
		var samplePanel = container.querySelector('[data-mode-panel="sample"]');
		var wholesaleBadge = container.querySelector('[data-mode-badge="wholesale"]');
		var sampleBadge = container.querySelector('[data-mode-badge="sample"]');
		
		// Also find badges in the parent (outside product-purchase div)
		var parentBadges = container.parentElement ? container.parentElement.querySelectorAll('[data-mode-badge]') : [];

		if (!wholesalePanel || !samplePanel) {
			return;
		}

		if (mode === 'sample') {
			animateModeTransition(wholesalePanel, samplePanel);

			if (wholesaleBadge) {
				wholesaleBadge.classList.add('is-hidden');
			}

			if (sampleBadge) {
				sampleBadge.classList.remove('is-hidden');
			}

			// Update parent badges
			parentBadges.forEach(function(badge) {
				if (badge.getAttribute('data-mode-badge') === 'wholesale') {
					badge.classList.add('is-hidden');
				} else if (badge.getAttribute('data-mode-badge') === 'sample') {
					badge.classList.remove('is-hidden');
				}
			});
		} else {
			animateModeTransition(samplePanel, wholesalePanel);

			if (sampleBadge) {
				sampleBadge.classList.add('is-hidden');
			}

			if (wholesaleBadge) {
				wholesaleBadge.classList.remove('is-hidden');
			}

			// Update parent badges
			parentBadges.forEach(function(badge) {
				if (badge.getAttribute('data-mode-badge') === 'sample') {
					badge.classList.add('is-hidden');
				} else if (badge.getAttribute('data-mode-badge') === 'wholesale') {
					badge.classList.remove('is-hidden');
				}
			});
		}
	}

	document.addEventListener('click', function(event) {
		var passwordToggle = event.target.closest('[data-password-toggle]');

		if (passwordToggle) {
			var targetId = passwordToggle.getAttribute('data-target');
			var passwordInput = targetId ? document.getElementById(targetId) : null;

			if (!passwordInput) {
				return;
			}

			var shouldShowPassword = passwordInput.type === 'password';
			passwordInput.type = shouldShowPassword ? 'text' : 'password';
			passwordToggle.classList.toggle('is-visible', shouldShowPassword);
			passwordToggle.setAttribute('aria-pressed', shouldShowPassword ? 'true' : 'false');
			passwordToggle.setAttribute('aria-label', shouldShowPassword ? 'Hide password' : 'Show password');
			return;
		}

		if (event.target.closest('.product-purchase__size-guide')) {
			updateSizeGuideDialogOffset();
		}

		var switchButton = event.target.closest('[data-switch-mode]');

		if (switchButton) {
			var switchContainer = switchButton.closest('[data-product-purchase]');

			if (switchContainer) {
				switchMode(switchContainer, switchButton.getAttribute('data-switch-mode'));
			}

			return;
		}

		var qtyButton = event.target.closest('[data-sample-qty-adjust]');

		if (qtyButton) {
			var qtyContainer = qtyButton.closest('[data-product-purchase]');
			var qtyInput = qtyContainer ? qtyContainer.querySelector('[data-sample-qty]') : null;

			if (qtyInput) {
				var current = parseInt(qtyInput.value || '1', 10);
				var delta = parseInt(qtyButton.getAttribute('data-sample-qty-adjust') || '0', 10);
				var nextValue = Number.isNaN(current) ? 1 : current + delta;

				qtyInput.value = Math.max(1, nextValue);
			}

			return;
		}

		var wholesaleButton = event.target.closest('[data-wholesale-add]');

		if (wholesaleButton) {
			var wholesaleContainer = wholesaleButton.closest('[data-product-purchase]');
			var wholesaleForm = getProductForm(wholesaleContainer);
			submitProductForm(wholesaleForm);
			return;
		}

		var sampleButton = event.target.closest('[data-sample-add]');

		if (sampleButton) {
			var sampleContainer = sampleButton.closest('[data-product-purchase]');
			var sampleForm = getProductForm(sampleContainer);
			var sampleQtyInput = sampleContainer ? sampleContainer.querySelector('[data-sample-qty]') : null;
			var sampleQuantity = sampleQtyInput ? parseInt(sampleQtyInput.value || '1', 10) : 1;

			setFormQuantity(sampleForm, Number.isNaN(sampleQuantity) ? 1 : Math.max(1, sampleQuantity));
			
			// Add preloader animation to button
			sampleButton.classList.add('sample-adding');
			sampleButton.disabled = true;
			sampleButton.setAttribute('data-loading', 'true');
			
			// Submit form
			submitProductForm(sampleForm);
			
			return;
		}

		var sizeGuideTabButton = event.target.closest('[data-size-guide-tab]');

		if (sizeGuideTabButton) {
			var dialog = sizeGuideTabButton.closest('.product-size-guide-dialog');
			var category = sizeGuideTabButton.getAttribute('data-size-guide-tab');

			applySizeGuideTabState(dialog, category);
			return;
		}

		var sampleColorSwatch = event.target.closest('[data-sample-color]');
		if (!sampleColorSwatch) {
			sampleColorSwatch = event.target.closest('[data-sample-color-link]');
		}

		if (sampleColorSwatch) {
			var colorProductLink = sampleColorSwatch.getAttribute('data-sample-color-link');

			if (colorProductLink) {
    			window.open(colorProductLink, '_blank', 'noopener,noreferrer');
    			return;
			}

			if (sampleColorSwatch.classList.contains('is-unavailable')) {
				return;
			}

			var sampleContainerForColor = sampleColorSwatch.closest('[data-product-purchase]');
			var colorOptionPosition = sampleColorSwatch.getAttribute('data-option-position');
			var colorName = sampleColorSwatch.getAttribute('data-option-value');
			var colorNameElement = sampleContainerForColor ? sampleContainerForColor.querySelector('[data-sample-color-name]') : null;

			if (sampleContainerForColor) {
				setActivePurchaseButton(sampleContainerForColor, '[data-sample-color]', sampleColorSwatch);
				setVariantOption(sampleContainerForColor, colorOptionPosition, colorName);
			}

			if (colorNameElement) {
				colorNameElement.textContent = colorName;
			}

			syncPurchaseOptionState(sampleContainerForColor);
			return;
		}

		var sampleSizeButton = event.target.closest('[data-sample-size]');

		if (sampleSizeButton) {
			if (sampleSizeButton.classList.contains('is-unavailable')) {
				return;
			}

			var sampleContainerForSize = sampleSizeButton.closest('[data-product-purchase]');
			var sizeOptionPosition = sampleSizeButton.getAttribute('data-option-position');
			var sizeOptionValue = sampleSizeButton.getAttribute('data-option-value');

			if (sampleContainerForSize) {
				setActivePurchaseButton(sampleContainerForSize, '[data-sample-size]', sampleSizeButton);
				setVariantOption(sampleContainerForSize, sizeOptionPosition, sizeOptionValue);
			}

			syncPurchaseOptionState(sampleContainerForSize);
			return;
		}
	});

	document.addEventListener('change', function(event) {
		var sampleSizeSelect = event.target.closest('[data-sample-size-select]');

		if (!sampleSizeSelect) {
			return;
		}

		var sampleContainer = sampleSizeSelect.closest('[data-product-purchase]');
		var optionPosition = sampleSizeSelect.getAttribute('data-option-position');

		setVariantOption(sampleContainer, optionPosition, sampleSizeSelect.value);
	});

	document.addEventListener('DOMContentLoaded', function() {
		syncEnhancedPurchaseUI();
		initSizeGuideTabs();
		updateSizeGuideDialogOffset();
	});

	document.addEventListener('variant:changed', function() {
		setTimeout(function() {
			syncEnhancedPurchaseUI();
			initSizeGuideTabs();
			updateSizeGuideDialogOffset();
		}, 50);
	});

	window.addEventListener('resize', function() {
		updateSizeGuideDialogOffset();
	});

	// ========================================
	// WHOLESALE ENQUIRY FORM FUNCTIONALITY
	// ========================================

	var COUNTRY_FLAGS = {
		'US': '🇺🇸', 'GB': '🇬🇧', 'CA': '🇨🇦', 'AU': '🇦🇺', 'NZ': '🇳🇿', 
		'AE': '🇦🇪', 'SA': '🇸🇦', 'QA': '🇶🇦', 'KW': '🇰🇼', 'BH': '🇧🇭',
		'OM': '🇴🇲', 'JO': '🇯🇴', 'SG': '🇸🇬', 'MY': '🇲🇾', 'TH': '🇹🇭',
		'IN': '🇮🇳', 'PK': '🇵🇰', 'BD': '🇧🇩', 'NL': '🇳🇱', 'DE': '🇩🇪',
		'FR': '🇫🇷', 'IT': '🇮🇹', 'ES': '🇪🇸', 'SE': '🇸🇪', 'NO': '🇳🇴'
	};

	var COUNTRY_CODES = {
		'US': '+1', 'GB': '+44', 'CA': '+1', 'AU': '+61', 'NZ': '+64', 
		'AE': '+971', 'SA': '+966', 'QA': '+974', 'KW': '+965', 'BH': '+973',
		'OM': '+968', 'JO': '+962', 'SG': '+65', 'MY': '+60', 'TH': '+66',
		'IN': '+91', 'PK': '+92', 'BD': '+880', 'NL': '+31', 'DE': '+49',
		'FR': '+33', 'IT': '+39', 'ES': '+34', 'SE': '+46', 'NO': '+47'
	};

	var WHOLESALE_COUNTRIES = [
		{ code: 'US', name: 'United States' },
		{ code: 'GB', name: 'United Kingdom' },
		{ code: 'CA', name: 'Canada' },
		{ code: 'AU', name: 'Australia' },
		{ code: 'NZ', name: 'New Zealand' },
		{ code: 'AE', name: 'United Arab Emirates' },
		{ code: 'SA', name: 'Saudi Arabia' },
		{ code: 'QA', name: 'Qatar' },
		{ code: 'KW', name: 'Kuwait' },
		{ code: 'BH', name: 'Bahrain' },
		{ code: 'OM', name: 'Oman' },
		{ code: 'JO', name: 'Jordan' },
		{ code: 'SG', name: 'Singapore' },
		{ code: 'MY', name: 'Malaysia' },
		{ code: 'TH', name: 'Thailand' },
		{ code: 'IN', name: 'India' },
		{ code: 'PK', name: 'Pakistan' },
		{ code: 'BD', name: 'Bangladesh' },
		{ code: 'NL', name: 'Netherlands' },
		{ code: 'DE', name: 'Germany' },
		{ code: 'FR', name: 'France' },
		{ code: 'IT', name: 'Italy' },
		{ code: 'ES', name: 'Spain' },
		{ code: 'SE', name: 'Sweden' },
		{ code: 'NO', name: 'Norway' }
	];

	function getCountryName(countryCode) {
		for (var index = 0; index < WHOLESALE_COUNTRIES.length; index += 1) {
			if (WHOLESALE_COUNTRIES[index].code === countryCode) {
				return WHOLESALE_COUNTRIES[index].name;
			}
		}

		return '';
	}

function initWholesaleEnquiryForm() {
  var forms = document.querySelectorAll('[data-wholesale-enquiry-form]');

  forms.forEach(function(formContainer) {
    var form = formContainer.querySelector('[data-wholesale-form]');

    if (!form) return;

    // Populate country selector
    initCountrySelector(formContainer);

    // Message preset buttons
    var messageBadges = formContainer.querySelectorAll('[data-message-template]');

    messageBadges.forEach(function(badge) {
      badge.addEventListener('click', function(e) {
        e.preventDefault();

        messageBadges.forEach(function(btn) {
          btn.classList.remove('is-active');
        });

        badge.classList.add('is-active');

        var textarea =
          form.querySelector('[name="contact[body]"]') ||
          form.querySelector('[name="message"]');

        if (textarea) {
          textarea.value = badge.getAttribute('data-message-template');
          textarea.dispatchEvent(new Event('input', { bubbles: true }));
          textarea.focus();
        }
      });
    });

    // Validation
    var fields = form.querySelectorAll('input, textarea, select');

    fields.forEach(function(field) {
      field.addEventListener('blur', function() {
        validateWholesaleField(field, form);
      });

      field.addEventListener('input', function() {
        if (field.classList.contains('has-error')) {
          validateWholesaleField(field, form);
        }
      });

      field.addEventListener('change', function() {
        validateWholesaleField(field, form);
      });
    });

    // Submit handler
    form.addEventListener('submit', function(e) {
      e.preventDefault();

	  var isValid = true;

      fields.forEach(function(field) {
        if (!validateWholesaleField(field, form)) {
          isValid = false;
        }
      });

      if (!isValid) {
        e.preventDefault();
        return;
      }

      var submitBtn = form.querySelector('[type="submit"]');

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('loading');

        submitBtn.innerHTML =
          '<span class="wholesale-enquiry-submit-text">Sending...</span>';
      }
	  
	    // // Success state from Shopify
		// if (formContainer.querySelector('.wholesale-enquiry-success')) {
		// var successBox = formContainer.querySelector('.wholesale-enquiry-success');

		// if (successBox) {
		// 	successBox.classList.remove('is-hidden');
		// }
		// }
		// return;
    });
  });
}
function validateWholesaleField(field, form) {
  var name = field.name || '';
  var value = (field.value || '').trim();

  var isValid = true;
  var errorMessage = '';

  if (name.indexOf('email') > -1) {
    var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!value) {
      isValid = false;
      errorMessage = 'Email is required';
    } else if (!emailRegex.test(value)) {
      isValid = false;
      errorMessage = 'Enter a valid email';
    }
  }

  if (
    name.indexOf('Phone') > -1 ||
    name.indexOf('phone') > -1
  ) {
    var digits = value.replace(/\D/g, '');

    if (!digits.length) {
      isValid = false;
      errorMessage = 'Phone number is required';
    } else if (digits.length < 7) {
      isValid = false;
      errorMessage = 'Enter a valid phone number';
    }
  }

  if (
    name.indexOf('body') > -1 ||
    name.indexOf('message') > -1
  ) {
    if (!value) {
      isValid = false;
      errorMessage = 'Message is required';
    } else if (value.length < 10) {
      isValid = false;
      errorMessage = 'Message must be at least 10 characters';
    }
  }

  var errorElement = null;

  if (name.indexOf('body') > -1) {
    errorElement = form.querySelector('[data-error-for="message"]');
  } else if (name.indexOf('email') > -1) {
    errorElement = form.querySelector('[data-error-for="email"]');
  } else if (
    name.indexOf('Phone') > -1 ||
    name.indexOf('phone') > -1
  ) {
    errorElement = form.querySelector('[data-error-for="phone"]');
  }

  if (isValid) {
    field.classList.remove('has-error');

    if (errorElement) {
      errorElement.textContent = '';
      errorElement.classList.remove('show');
    }
  } else {
    field.classList.add('has-error');

    if (errorElement) {
      errorElement.textContent = errorMessage;
      errorElement.classList.add('show');
    }
  }

  return isValid;
}
	// function prefillEnquiryForm(form) {
	// 	// Check if customer data is available in window object
	// 	if (typeof Shopify !== 'undefined' && Shopify.customer) {
	// 		var customer = Shopify.customer;

	// 		if (customer.first_name) {
	// 			var firstNameField = form.querySelector('[name="first_name"]');
	// 			if (firstNameField) firstNameField.value = customer.first_name;
	// 		}

	// 		if (customer.last_name) {
	// 			var lastNameField = form.querySelector('[name="last_name"]');
	// 			if (lastNameField) lastNameField.value = customer.last_name;
	// 		}

	// 		if (customer.email) {
	// 			var emailField = form.querySelector('[name="email"]');
	// 			if (emailField) emailField.value = customer.email;
	// 		}

	// 		// Try to set country if available
	// 		if (customer.default_address && customer.default_address.country_code) {
	// 			var countrySelect = form.querySelector('[name="country"]');
	// 			if (countrySelect) {
	// 				setTimeout(function() {
	// 					countrySelect.value = customer.default_address.country_code;
	// 					updateCountryFlag(form);
	// 				}, 100);
	// 			}
	// 		}
	// 	}
	// }

function initCountrySelector(formContainer) {
  var countrySelect =
    formContainer.querySelector('[name="country"]') ||
    formContainer.querySelector('[name="contact[Country Code]"]') ||
    formContainer.querySelector('.wholesale-enquiry-country-select');

  if (!countrySelect) {
    console.log('Country select not found');
    return;
  }

  // remove existing options except first
  countrySelect.innerHTML = '<option value="">Code</option>';

  WHOLESALE_COUNTRIES.forEach(function(country) {
    var option = document.createElement('option');

    option.value = country.code;

    option.textContent =
      (COUNTRY_FLAGS[country.code] || '') +
      ' ' +
      (COUNTRY_CODES[country.code] || '');

    countrySelect.appendChild(option);
  });

  countrySelect.value = 'AE';

  countrySelect.addEventListener('change', function() {
    updateCountryFlag(formContainer);
  });

  updateCountryFlag(formContainer);
}

function updateCountryFlag(formContainer) {
  var countrySelect =
    formContainer.querySelector('[name="country"]') ||
    formContainer.querySelector('[name="contact[Country Code]"]') ||
    formContainer.querySelector('.wholesale-enquiry-country-select');

  var phoneInput =
    formContainer.querySelector('[name="phone"]') ||
    formContainer.querySelector('[name="contact[Phone]"]');

  if (!countrySelect) return;

  var countryCode = countrySelect.value;

  if (phoneInput) {
    phoneInput.setAttribute(
      'data-dial-code',
      COUNTRY_CODES[countryCode] || ''
    );
  }
}

	function validateField(field, form) {
		var name = field.name;
		var value = field.value.trim();
		var isValid = true;
		var errorMsg = '';
		var errorKey = name === 'country' ? 'phone' : name;
		var controlElement = (name === 'phone' || name === 'country') ? (field.closest('.wholesale-enquiry-phone-input-wrapper') || field) : field;

		switch (name) {
			case 'first_name':
			case 'last_name':
				if (!value) {
					isValid = false;
					errorMsg = 'This field is required';
				} else if (value.length < 2) {
					isValid = false;
					errorMsg = 'Please enter at least 2 characters';
				}
				break;

			case 'email':
				var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
				if (!value) {
					isValid = false;
					errorMsg = 'Email is required';
				} else if (!emailRegex.test(value)) {
					isValid = false;
					errorMsg = 'Please enter a valid email address';
				}
				break;

			case 'phone':
				var digitsOnly = value.replace(/\D/g, '');
				if (!value) {
					isValid = false;
					errorMsg = 'Phone number is required';
				} else if (digitsOnly.length < 7) {
					isValid = false;
					errorMsg = 'Please enter a valid phone number';
				}
				break;

			case 'country':
				if (!value) {
					isValid = false;
					errorMsg = 'Please choose a country code';
				}
				break;

			case 'message':
				if (!value) {
					isValid = false;
					errorMsg = 'Please enter a message';
				} else if (value.length < 10) {
					isValid = false;
					errorMsg = 'Message must be at least 10 characters';
				}
				break;
		}

		var errorElement = form.querySelector('[data-error-for="' + errorKey + '"]');
		if (isValid) {
			field.classList.remove('has-error');
			controlElement.classList.remove('has-error');
			if (errorElement) {
				errorElement.classList.remove('show');
				errorElement.textContent = '';
			}
		} else {
			field.classList.add('has-error');
			controlElement.classList.add('has-error');
			if (errorElement) {
				errorElement.classList.add('show');
				errorElement.textContent = errorMsg;
			}
		}

		return isValid;
	}

	function validateForm(form) {
		var fields = form.querySelectorAll('[data-enquiry-field]');
		var allValid = true;

		fields.forEach(function(field) {
			if (!validateField(field, form)) {
				allValid = false;
			}
		});

		return allValid;
	}

	function handleWholesaleEnquirySubmit(formContainer, form) {
		if (!validateForm(form)) {
			return;
		}

		var submitBtn = form.querySelector('[type="submit"]');
		var successMsg = formContainer.querySelector('[data-enquiry-success]');

		// Get form data
		var formData = new FormData(form);
		var countryCode = formData.get('country');
		var countryDialCode = COUNTRY_CODES[countryCode] || '';
		var localPhoneNumber = (formData.get('phone') || '').trim();
		var data = {
			first_name: formData.get('first_name'),
			last_name: formData.get('last_name'),
			email: formData.get('email'),
			phone: (countryDialCode ? countryDialCode + ' ' : '') + localPhoneNumber,
			phone_local: localPhoneNumber,
			country: countryCode,
			country_name: getCountryName(countryCode),
			country_dial_code: countryDialCode,
			message: formData.get('message'),
			product_id: formContainer.getAttribute('data-product-id'),
			product_title: formContainer.getAttribute('data-product-title'),
			product_ref: formContainer.getAttribute('data-product-ref'),
			timestamp: new Date().toISOString(),
			customer_email: Shopify.customer ? Shopify.customer.email : 'Not logged in'
		};

		// Disable submit button
		submitBtn.disabled = true;
		submitBtn.classList.add('loading');
		submitBtn.innerHTML = '<span class="wholesale-enquiry-submit-text">Sending...</span>';

		// Submit enquiry via email
		// submitEnquiryViaEmail(data, function(success) {
		// 	submitBtn.disabled = false;
		// 	submitBtn.classList.remove('loading');
		// 	submitBtn.innerHTML = '<span class="wholesale-enquiry-submit-text">Send Inquiry</span>';

		// 	if (success) {
		// 		// Show success message
		// 		form.style.display = 'none';
		// 		if (successMsg) {
		// 			successMsg.classList.remove('is-hidden');
		// 		}

		// 		// Reset form after 3 seconds and close modal
		// 		setTimeout(function() {
		// 			form.reset();
		// 			var countrySelect = form.querySelector('[name="country"]');
		// 			if (countrySelect) {
		// 				countrySelect.value = 'AE';
		// 			}
		// 			updateCountryFlag(formContainer);
		// 			formContainer.querySelectorAll('[data-message-template]').forEach(function(badge) {
		// 				badge.classList.remove('is-active');
		// 			});
		// 			form.style.display = 'flex';
		// 			if (successMsg) {
		// 				successMsg.classList.add('is-hidden');
		// 			}
		// 			// Close popover modal
		// 			var modal = formContainer.closest('[data-wholesale-enquiry-form]');
		// 			if (modal) {
		// 				modal.removeAttribute('open');
		// 				modal.setAttribute('hidden', '');
		// 			}
		// 		}, 3000);
		// 	} else {
		// 		alert('Error submitting enquiry. Please try again or contact support.');
		// 	}
		// });
	}

// 	function submitEnquiryViaEmail(data, callback) {
//   var contactBody = `
// Wholesale Enquiry

// Product: ${data.product_title || ''} (Ref: ${data.product_ref || ''})

// Customer: ${data.first_name || ''} ${data.last_name || ''}
// Email: ${data.email || ''}
// Phone: ${data.phone || ''}
// Country: ${data.country_name || ''}

// Message:
// ${data.message || ''}

// Timestamp: ${data.timestamp || ''}
// `;

//   var payload = new URLSearchParams();

//   payload.append('form_type', 'contact');
//   payload.append('utf8', '✓');
//   payload.append('contact[name]', `${data.first_name} ${data.last_name}`);
//   payload.append('contact[email]', data.email);
//   payload.append('contact[phone]', data.phone);
//   payload.append('contact[body]', contactBody);

//   fetch('/contact', {
//     method: 'POST',
//     headers: {
//       'Content-Type': 'application/x-www-form-urlencoded'
//     },
//     body: payload.toString()
//   })
//     .then(function(response) {
// 		console.log(response);

// 		if (response.status >= 200 && response.status < 400) {
// 			callback(true);
// 		} else {
// 			callback(false);
// 		}
// 	})
//     .catch(function(error) {
//       console.error(error);
//       callback(false);
//     });
// }

	// function sendEnquiryNotification(data) {
	// 	// Log to console for debugging
	// 	console.log('Wholesale Enquiry Submitted:', data);
		
	// 	// You can add analytics tracking here
	// 	if (window.gtag) {
	// 		window.gtag('event', 'wholesale_enquiry_submitted', {
	// 			product_id: data.product_id,
	// 			product_title: data.product_title
	// 		});
	// 	}
	// }

	// Remove preloader when item is added to cart
	document.addEventListener('variant:added', function() {
		var loadingButtons = document.querySelectorAll('[data-sample-add][data-loading="true"]');
		loadingButtons.forEach(function(btn) {
			btn.classList.remove('sample-adding');
			btn.disabled = false;
			btn.removeAttribute('data-loading');
		});
	});

	// Initialize on page load
	document.addEventListener('DOMContentLoaded', function() {
		initWholesaleEnquiryForm();
	});
})();
// ─────────────────────────────────────────────────────────────────
// CUSTOMER ACCOUNT PORTAL — Sidebar toggle
// ─────────────────────────────────────────────────────────────────
(function() {
  function initAccountPortal() {
    var sidebar = document.getElementById('acc-sidebar');
    var overlay = document.getElementById('acc-sidebar-overlay');
    var toggleBtn = document.getElementById('acc-mobile-toggle');
    var closeBtn = document.getElementById('acc-sidebar-close');
		var navLinks = sidebar ? sidebar.querySelectorAll('.acc-sidebar__link, .acc-sidebar__logout') : [];
    if (!sidebar) return;

		function setScrollLock(isLocked) {
			document.documentElement.classList.toggle('acc-sidebar-open', isLocked);
			document.body.classList.toggle('acc-sidebar-open', isLocked);
		}

    function openSidebar() {
      sidebar.classList.add('is-open');
      if (overlay) overlay.classList.add('is-visible');
			setScrollLock(true);
      if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'true');
    }

    function closeSidebar() {
      sidebar.classList.remove('is-open');
      if (overlay) overlay.classList.remove('is-visible');
			setScrollLock(false);
      if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'false');
    }

    if (toggleBtn) toggleBtn.addEventListener('click', openSidebar);
    if (closeBtn) closeBtn.addEventListener('click', closeSidebar);
    if (overlay) overlay.addEventListener('click', closeSidebar);
		navLinks.forEach(function(link) {
			link.addEventListener('click', closeSidebar);
		});

    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && sidebar.classList.contains('is-open')) closeSidebar();
    });

    window.addEventListener('resize', function() {
      if (window.innerWidth > 1024 && sidebar.classList.contains('is-open')) closeSidebar();
    });

		window.addEventListener('pagehide', function() {
			setScrollLock(false);
		});

		if (window.innerWidth > 1024) {
			closeSidebar();
		}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAccountPortal);
  } else {
    initAccountPortal();
  }
})();
